import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { CHART, fmtCompact, fmtFull, labelIndices, niceTicks, shortDay } from './chart-kit';

export interface StackSeries {
  label: string;
  color: string;
}

export interface StackDay {
  date: string;
  /** One value per series, bottom to top. */
  segments: number[];
  /** Extra rows for the tooltip, below the series values. */
  details?: { label: string; value: string }[];
}

interface Bar {
  x: number;
  w: number;
  day: StackDay;
  total: number;
  rects: { y: number; h: number; color: string; top: boolean }[];
}

const HEIGHT = 240;
const AXIS_BAND = 26;
const LEFT = 52;
const RIGHT = 8;
const TOP = 18;

/**
 * Stacked columns, one per day: thin (<= 24px) bars from one baseline, a 2px
 * surface gap between segments, a 4px rounded data end, square at the
 * baseline. Each day's column is its own hover/focus target (the whole plot
 * height, wider than the bar) and shows every series plus the day's details.
 * Only the highest day carries a direct label; the table below has the rest.
 */
@Component({
  selector: 'app-stack-chart',
  template: `
    <div class="legend">
      @for (s of series(); track s.label) {
      <span class="key"><span class="swatch" [style.background]="s.color"></span>{{ s.label }}</span>
      }
    </div>
    <div class="plot" (pointerleave)="hover.set(null)">
      <svg [attr.width]="width()" [attr.height]="height" role="img" [attr.aria-label]="ariaLabel()">
        @for (t of ticks(); track t) {
        <line [attr.x1]="left" [attr.x2]="width() - right" [attr.y1]="y(t)" [attr.y2]="y(t)"
          [attr.stroke]="t === 0 ? c.axis : c.grid" stroke-width="1" />
        <text [attr.x]="left - 8" [attr.y]="y(t) + 4" text-anchor="end" class="tick">{{ compact(t) }}</text>
        }
        @for (b of bars(); track b.day.date; let i = $index) {
        @if (hover() === i) {
        <rect [attr.x]="b.x - bandPad()" [attr.y]="top" [attr.width]="b.w + bandPad() * 2" [attr.height]="plotH()" class="hover-band" />
        }
        @for (r of b.rects; track $index) {
        <path [attr.d]="segPath(b.x, r.y, b.w, r.h, r.top)" [attr.fill]="r.color" />
        }
        }
        @if (peak(); as p) {
        <text [attr.x]="p.x + p.w / 2" [attr.y]="p.y - 6" text-anchor="middle" class="peak">{{ compact(p.total) }}</text>
        }
        @for (i of xLabels(); track i) {
        <text [attr.x]="bars()[i].x + bars()[i].w / 2" [attr.y]="baseline() + 18" text-anchor="middle" class="tick">{{ short(bars()[i].day.date) }}</text>
        }
        @for (b of bars(); track b.day.date; let i = $index) {
        <rect [attr.x]="b.x - bandPad()" [attr.y]="top" [attr.width]="b.w + bandPad() * 2" [attr.height]="plotH() + axisBand"
          fill="transparent" tabindex="0" class="hit" [attr.aria-label]="b.day.date + ': ' + full(b.total)"
          (pointerenter)="hover.set(i)" (focus)="hover.set(i)" (blur)="hover.set(null)" />
        }
      </svg>
      @if (hovered(); as h) {
      <div class="tip" [style.left.px]="tipLeft()" [style.top.px]="top">
        <div class="tip-date">{{ h.day.date }}</div>
        <div class="tip-row total"><b>{{ full(h.total) }}</b><span>{{ totalLabel() }}</span></div>
        @for (s of series(); track s.label; let si = $index) {
        <div class="tip-row"><span class="line-key" [style.background]="s.color"></span><b>{{ full(h.day.segments[si]) }}</b><span>{{ s.label }}</span></div>
        }
        @for (d of h.day.details ?? []; track d.label) {
        <div class="tip-row detail"><b>{{ d.value }}</b><span>{{ d.label }}</span></div>
        }
      </div>
      }
    </div>
  `,
  styleUrl: './chart.scss',
})
export class StackChart implements AfterViewInit, OnDestroy {
  readonly days = input.required<StackDay[]>();
  readonly series = input.required<StackSeries[]>();
  readonly totalLabel = input('Total');
  readonly ariaLabel = input('Stacked column chart');

  readonly c = CHART;
  readonly height = HEIGHT + AXIS_BAND;
  readonly top = TOP;
  readonly left = LEFT;
  readonly right = RIGHT;
  readonly axisBand = AXIS_BAND;
  readonly compact = fmtCompact;
  readonly full = fmtFull;
  readonly short = shortDay;

  private host = inject(ElementRef<HTMLElement>);
  private observer?: ResizeObserver;
  readonly width = signal(600);
  readonly hover = signal<number | null>(null);

  readonly plotH = computed(() => HEIGHT - TOP);
  readonly baseline = computed(() => HEIGHT);
  readonly ticks = computed(() => niceTicks(Math.max(0, ...this.days().map(d => d.segments.reduce((a, v) => a + Math.max(0, v), 0)))));
  private readonly yMax = computed(() => this.ticks()[this.ticks().length - 1] || 1);

  y(v: number): number {
    return HEIGHT - (v / this.yMax()) * this.plotH();
  }

  private readonly band = computed(() => (this.width() - LEFT - RIGHT) / Math.max(1, this.days().length));
  readonly bandPad = computed(() => Math.max(0, (this.band() - this.barW()) / 2));
  private readonly barW = computed(() => Math.max(1, Math.min(24, this.band() * 0.64)));

  readonly bars = computed<Bar[]>(() => {
    const w = this.barW();
    return this.days().map((day, i) => {
      const x = LEFT + i * this.band() + (this.band() - w) / 2;
      const total = day.segments.reduce((a, v) => a + Math.max(0, v), 0);
      const nonZero = day.segments.map((v, si) => ({ v: Math.max(0, v), si })).filter(s => s.v > 0);
      let acc = 0;
      const rects = nonZero.map((s, k) => {
        const y0 = this.y(acc);
        acc += s.v;
        const y1 = this.y(acc);
        // The 2px surface gap sits below every segment except the first.
        const gap = k > 0 && y0 - y1 > 3 ? 2 : 0;
        return { y: y1, h: Math.max(0, y0 - y1 - gap), color: this.series()[s.si]?.color ?? CHART.muted, top: k === nonZero.length - 1 };
      });
      return { x, w, day, total, rects };
    });
  });

  readonly peak = computed(() => {
    const bars = this.bars();
    if (bars.length === 0) return null;
    const b = bars.reduce((m, x) => (x.total > m.total ? x : m));
    return b.total > 0 ? { ...b, y: this.y(b.total) } : null;
  });

  readonly xLabels = computed(() => labelIndices(this.bars().length, Math.max(2, Math.floor((this.width() - LEFT) / 64))));

  readonly hovered = computed(() => {
    const i = this.hover();
    return i === null ? null : this.bars()[i] ?? null;
  });

  /** Keeps the tooltip inside the plot: right of the bar, or left of it near the edge. */
  readonly tipLeft = computed(() => {
    const b = this.hovered();
    if (!b) return 0;
    const tipW = 200;
    const right = b.x + b.w + 12;
    return right + tipW > this.width() ? Math.max(0, b.x - tipW - 12) : right;
  });

  /** A segment path: square corners, except a 4px round on the top of the topmost segment. */
  segPath(x: number, y: number, w: number, h: number, roundTop: boolean): string {
    if (h <= 0) return '';
    const r = roundTop ? Math.min(4, w / 2, h) : 0;
    return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
  }

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver(entries => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) this.width.set(w);
    });
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
